import React, { CSSProperties, FC, useState } from 'react';
import { useAppSelector } from '../store';
import { Wizard, WizardStepMeta } from '../wizard';
// eslint-disable-next-line max-len
import { HorizontalChoiceGroup } from '../components/common/horizontalChoiceGroup';
import { ShareContext } from '../store/reducers/conversation/reducer';
import { withUserConfigGuard } from '../hoc/userConfigGuard';
// eslint-disable-next-line max-len
import {
    OrganizationSharePageProps,
    OrganizationSharePage,
    ShareTypePageVariant,
} from './organizationShare';
import { PublicSharePage, PublicSharePageProps } from './publicShare';
import { withTabs } from '../hoc/tabs';
import { Stack } from '@fluentui/react';
import { validateFormField } from '../helpers/validation';
import {
    selectDestinationFolderPath,
    selectFilesToUpload,
} from '../store/selectors/app';
import { SelectFileStep } from '../components/wizard/steps/selectFile';
import { WarningDialog } from '../components/dialogs';
import { useCustomValidationDialog } from '../helpers/hooks';
import { destinationFolderSchema, dndFilesSchema } from '../schemas/shared';
import { WizardPageHeading } from '../components/wizard/pageHeading';
import { ValidationError } from '../helpers/model/validation';
import {
    selectShareContext,
    selectTeamsContext,
} from '../store/selectors/conversation';
import { withConversationContextGuard } from '../hoc/conversationContextGuard';
import { withLicenseLevelGuard } from '../hoc/licenseLevelGuard';
import { LicenseLevel } from 'nextcloud-api';
import { selectTranslate } from '../store/selectors/ui';
import { withDefaultFolderData } from '../components/fileExplorer/withDefaultFolderData';

const shareContextToPageMap = {
    [ShareContext.Organization]: OrganizationSharePage,
    [ShareContext.Public]: PublicSharePage,
    [ShareContext.Any]: PublicSharePage,
} as const satisfies Record<
    ShareContext,
    FC<PublicSharePageProps | OrganizationSharePageProps>
>;

const horizontalChoiceGroupStyle: CSSProperties = { marginBottom: 12 };

type ShareTypeSelectorProps = {
    variant: ShareTypePageVariant;
};

export const ShareTypeSelector = ({
    variant,
}: ShareTypeSelectorProps): JSX.Element => {
    const shareContext = useAppSelector(selectShareContext);
    const teamsContext = useAppSelector(selectTeamsContext);
    const translate = useAppSelector(selectTranslate);

    const isChannelOrTeam = Boolean(
        teamsContext?.channel?.id || teamsContext?.team?.groupId,
    );

    const defaultSelectedKey = isChannelOrTeam
        ? ShareContext.Organization
        : !shareContext ||
            shareContext === ShareContext.Any ||
            shareContext === ShareContext.Public
          ? ShareContext.Public
          : ShareContext.Organization;

    const [contextToDisplay, setContextToDisplay] = useState<
        ShareContext.Public | ShareContext.Organization
    >(defaultSelectedKey);

    const Page = shareContextToPageMap[contextToDisplay];

    return (
        <>
            <WizardPageHeading />
            <HorizontalChoiceGroup
                style={horizontalChoiceGroupStyle}
                selectedKey={contextToDisplay}
                onChange={(ev, option) => setContextToDisplay(option.key)}
                options={[
                    {
                        key: ShareContext.Organization,
                        text: translate('organization'),
                    },
                    { key: ShareContext.Public, text: translate('public') },
                ]}
            />
            <Page variant={variant} />
        </>
    );
};

const SharePage = (): JSX.Element => {
    const files = useAppSelector(selectFilesToUpload);
    const destinationFolderPath = useAppSelector(selectDestinationFolderPath);

    const {
        firstError,
        setErrors,
        setShowValidationDialog,
        showValidationDialog,
    } = useCustomValidationDialog<'files'>();

    const validateFiles = async (): Promise<boolean> => {
        const fileErrors: ValidationError[] = [];

        const filesValid = await validateFormField(
            dndFilesSchema,
            files,
            (err) =>
                fileErrors.push({
                    type: 'file_length',
                    message: err.message ?? '',
                }),
        );

        const destinationFileValid = await validateFormField(
            destinationFolderSchema,
            destinationFolderPath,
            (err) =>
                fileErrors.push({
                    type: 'destination_folder',
                    message: err.message ?? '',
                }),
        );

        setErrors({
            files: fileErrors,
        });

        return filesValid && destinationFileValid;
    };

    const stepMetaArray: WizardStepMeta[] = [
        {
            tag: 'files',
            canMoveToNext: validateFiles,
        },
        { tag: 'form' },
    ];

    return (
        <Wizard.Provider stepMetaArr={stepMetaArray}>
            <Stack className="main-part">
                <Wizard.Steps>
                    <SelectFileStep />
                    <ShareTypeSelector variant={ShareTypePageVariant.Regular} />
                </Wizard.Steps>
            </Stack>
            <WarningDialog
                hidden={!showValidationDialog}
                subText={firstError?.message}
                icon="ErrorBadge"
                color="red"
                onDismiss={() => setShowValidationDialog(false)}
            />
        </Wizard.Provider>
    );
};

export default withTabs(
    withUserConfigGuard(
        withConversationContextGuard(
            withDefaultFolderData(
                withLicenseLevelGuard(SharePage, LicenseLevel.Standard),
            ),
        ),
    ),
);
